import {
  Box,
  Card,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvitationDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import InvitationActions from './InvitationActions';
import InvitationClinicCover from './InvitationClinicCover';
import InvitationContactRow from './InvitationContactRow';
import InvitationRoleTiles from './InvitationRoleTiles';
import InvitationValidity from './InvitationValidity';
import { InvitationDecisions } from './useInvitationDecisions';
import { useInvitationDetails } from './useInvitationDetails';

const VISIBLE_SPECIALIZATIONS = 4;

interface Props {
  invitation: InvitationDto;
  decisions: InvitationDecisions;
}

/** Profile-style card of the inviting clinic with the granted roles and the decision. */
export default function InvitationClinicCard({
  invitation,
  decisions,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const details = useInvitationDetails(invitation);
  const hidden = details.specializations.length - VISIBLE_SPECIALIZATIONS;

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <InvitationClinicCover details={details} />
      <Stack gap={3} sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        <Box>
          <Typography component="h2" variant="h4">
            {details.name}
          </Typography>
          {details.isLoading ? (
            <Skeleton width="50%" />
          ) : (
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              {details.city && (
                <Typography variant="body2" color="text.secondary">
                  {details.city}
                </Typography>
              )}
              {details.opening && (
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: details.opening.isOpen
                        ? 'success.main'
                        : 'grey.500',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {details.opening.text}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        <Stack gap={1.5}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('invitationAcceptance.yourAccess')}
          </Typography>
          <InvitationRoleTiles roles={details.roles} />
        </Stack>

        {(details.address ||
          details.phoneNumber ||
          details.email ||
          details.website) && (
          <Stack gap={1}>
            {details.address && (
              <InvitationContactRow
                icon="solar:map-point-bold-duotone"
                label={translateKey('search.book.cardAddress')}
              >
                {details.address}
              </InvitationContactRow>
            )}
            {details.phoneNumber && (
              <InvitationContactRow
                icon="solar:phone-bold"
                label={translateKey('search.book.cardPhone')}
                href={`tel:${details.phoneNumber}`}
              >
                {details.phoneNumber}
              </InvitationContactRow>
            )}
            {details.email && (
              <InvitationContactRow
                icon="solar:letter-bold-duotone"
                label={translateKey('search.book.cardEmail')}
                href={`mailto:${details.email}`}
              >
                {details.email}
              </InvitationContactRow>
            )}
            {details.website && (
              <InvitationContactRow
                icon="solar:earth-bold-duotone"
                label={translateKey('search.ui.website')}
                href={
                  /^https?:\/\//.test(details.website)
                    ? details.website
                    : `https://${details.website}`
                }
                external
              >
                {details.website.replace(/^https?:\/\//, '')}
              </InvitationContactRow>
            )}
          </Stack>
        )}

        {details.specializations.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {details.specializations
              .slice(0, VISIBLE_SPECIALIZATIONS)
              .map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  variant="soft"
                  color="primary"
                />
              ))}
            {hidden > 0 && (
              <Chip
                label={t('invitationAcceptance.moreSpecializations', {
                  count: hidden,
                })}
                size="small"
                variant="soft"
              />
            )}
          </Stack>
        )}

        <Divider />
        <InvitationValidity details={details} />
        <InvitationActions decisions={decisions} />
      </Stack>
    </Card>
  );
}
